import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Archive, ArchiveRestore, ChevronLeft, MoreHorizontal, Pencil, ReceiptText } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button, buttonVariants } from "~/components/ui/button";
import { Menu } from "~/components/ui/menu";
import { Page, PageHeader, Panel, Stat } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { History } from "~/features/audit/history";
import { SupplierFormSheet, supplierQuery, type Supplier } from "~/features/suppliers/supplier";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";

export const Route = createFileRoute("/_app/suppliers/$id")({
  // Not awaited: the page renders its skeleton at once, then data or a retryable error.
  loader: ({ context, params }) => void context.queryClient.prefetchQuery(supplierQuery(params.id)),
  component: SupplierPage,
});

function BackLink() {
  const t = useT();
  return (
    <Link to="/suppliers" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("suppliers.title")}
    </Link>
  );
}

function SupplierPage() {
  const { id } = Route.useParams();
  const query = useQuery(supplierQuery(id));
  if (query.isPending)
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Page>
    );
  if (query.isError)
    return (
      <Page>
        <BackLink />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  return <SupplierView supplier={query.data} />;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-b border-line px-5 py-5 last:border-b-0 md:px-6">
      <h2 className="mb-3 text-[15px] font-medium">{title}</h2>
      {children}
    </div>
  );
}

function SupplierView({ supplier }: { supplier: Supplier }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const archived = Boolean(supplier.archivedAt);

  const products = useQuery({
    queryKey: ["products", "list", { supplierId: supplier.id }],
    queryFn: () => unwrap(api.products.get({ query: listQuery({ supplierId: supplier.id, limit: 200 }) })),
  });

  const toggleArchive = useMutation({
    mutationFn: () =>
      unwrap(archived ? api.suppliers({ id: supplier.id }).restore.post() : api.suppliers({ id: supplier.id }).archive.post()),
    onSuccess: (saved) => {
      queryClient.setQueryData(["suppliers", supplier.id], saved);
      void queryClient.invalidateQueries({ queryKey: ["suppliers", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["audit", "supplier", supplier.id] });
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  return (
    <Page>
      <PageHeader
        back={<BackLink />}
        title={supplier.name}
        meta={archived ? <Badge tone="warning">{t("customers.showArchived")}</Badge> : null}
        actions={
          <>
            {can("purchasing:read") ? (
              <Link
                to="/purchasing"
                search={{ supplierId: supplier.id, status: "all" }}
                className={buttonVariants({ variant: "secondary" })}
              >
                <ReceiptText />
                <span className="max-sm:sr-only">{t("suppliers.purchaseOrders")}</span>
              </Link>
            ) : null}
            {can("suppliers:write") ? (
              <>
                <Button onClick={() => setEditing(true)}>
                  <Pencil />
                  {t("common.actions.edit")}
                </Button>
                <Menu
                  trigger={
                    <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                      <MoreHorizontal />
                    </Button>
                  }
                  actions={[
                    {
                      label: archived ? t("common.actions.restore") : t("common.actions.archive"),
                      icon: archived ? <ArchiveRestore /> : <Archive />,
                      onSelect: () => toggleArchive.mutate(),
                    },
                  ]}
                />
              </>
            ) : null}
          </>
        }
      />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Panel>
          <Section title={t("customers.tabs.overview")}>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
              <Stat label={t("suppliers.fields.contactName")} value={supplier.contactName ?? "—"} />
              <Stat
                label={t("common.fields.email")}
                value={
                  supplier.email ? (
                    <a href={`mailto:${supplier.email}`} className="hover:underline">
                      {supplier.email}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              <Stat label={t("common.fields.phone")} value={<span className="tabular">{supplier.phone ?? "—"}</span>} />
              <Stat
                label={t("suppliers.fields.currency")}
                value={`${supplier.currency} — ${format.currency(supplier.currency)}`}
              />
              <Stat
                label={t("suppliers.fields.leadTimeDays")}
                value={
                  <span className="tabular">{supplier.leadTimeDays != null ? format.number(supplier.leadTimeDays) : "—"}</span>
                }
              />
              <Stat
                label={t("common.fields.website")}
                value={
                  supplier.website ? (
                    <a href={supplier.website} target="_blank" rel="noreferrer" className="hover:underline">
                      {supplier.website.replace(/^https?:\/\//, "")}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
            </dl>
          </Section>
          {supplier.address ? (
            <Section title={t("common.fields.address")}>
              <address className="text-sm not-italic leading-relaxed">
                {format.address(supplier.address).map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </address>
            </Section>
          ) : null}
          <Section title={t("nav.products")}>
            {products.isPending ? (
              <Skeleton className="h-4 w-1/2" />
            ) : products.isError ? (
              <ErrorState error={products.error} onRetry={() => void products.refetch()} />
            ) : products.data.items.length === 0 ? (
              <p className="text-sm text-fg-3">{t("products.emptyTitle")}</p>
            ) : (
              <ul className="-mx-2 flex flex-col">
                {products.data.items.map((p) => (
                  <li key={p.id}>
                    <Link
                      to="/products/$id"
                      params={{ id: p.id }}
                      className="flex min-h-11 items-center justify-between gap-3 rounded-control px-2 text-sm hover:bg-surface-2"
                    >
                      <span className="truncate">{p.title}</span>
                      <span className="shrink-0 text-xs text-fg-3">{t("products.variantCount", { count: p.variantCount })}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          {supplier.notes ? (
            <Section title={t("common.fields.notes")}>
              <p className="text-sm whitespace-pre-line text-fg-2">{supplier.notes}</p>
            </Section>
          ) : null}
        </Panel>
        <aside>
          <Panel>
            <Section title={t("audit.history")}>
              <History entityType="supplier" entityId={supplier.id} />
            </Section>
          </Panel>
        </aside>
      </div>
      <SupplierFormSheet
        open={editing}
        onOpenChange={setEditing}
        supplier={supplier}
        onSaved={(saved) => {
          queryClient.setQueryData(["suppliers", supplier.id], saved);
          void queryClient.invalidateQueries({ queryKey: ["audit", "supplier", supplier.id] });
        }}
      />
    </Page>
  );
}
